import { type ExploreSubjectSchemeUnit } from "@twinkltech/curriculum-architect-domain-hooks";
import { useTwinklAnalytics } from "@twinkltech/web-analytics-ts";
import {
  IconCommunicationInformation,
  IconNavigationArrowDown,
  IconNavigationArrowUp,
  Text,
} from "@twinkltech/web-ui";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { TagList } from "../TagList/TagList";
import { getCohortSlugTranslations } from "../TagList/utils/getCohortSlugTranslations/getCohortSlugTranslations";
import { getThreadSlugTranslations } from "../TagList/utils/getThreadSlugTranslations/getThreadSlugTranslations";

import { EXPLORE_SUBJECT_UNIT_CARD_NS } from "./ExploreSubjectUnitCard.i18n";

export const ExploreSubjectUnitCard = ({
  unit: {
    description,
    keyArtUrl,
    keyStagesSlug,
    learningSequence,
    threadSlugs,
    title,
  },
}: {
  unit: ExploreSubjectSchemeUnit;
}) => {
  const { t } = useTranslation(EXPLORE_SUBJECT_UNIT_CARD_NS);

  const { click } = useTwinklAnalytics();

  const [isOverviewVisible, setOverviewVisible] = useState<boolean>(false);

  const cardLabelId = useId();

  const tags = [
    t("learning-sequence.count", {
      count: learningSequence.length,
    }),
    ...getCohortSlugTranslations(keyStagesSlug),
    ...getThreadSlugTranslations(threadSlugs),
  ];

  return (
    <div
      aria-labelledby={cardLabelId}
      className="rounded-lg flex border-neutral-150 border flex-col grow gap-6 focus-within:border-neutral-100 py-3 pl-3 pr-8"
      role="article"
    >
      <div className="flex gap-6">
        <div
          className="min-w-[153px] w-[153px] shrink-0 aspect-square rounded bg-cover bg-no-repeat bg-center bg-neutral-100 border"
          role="presentation"
          style={{
            backgroundImage: `url(${keyArtUrl})`,
          }}
        />
        <div className="max-w-[800px] flex flex-col gap-2 justify-center">
          <Text
            as="h2"
            className="line-clamp-1"
            id={cardLabelId}
            variant="lg-medium"
          >
            {title}
          </Text>
          <div className="flex gap-2" role="group">
            <TagList tags={new Set(tags)} variant="dark" />
          </div>
        </div>
        <div
          className="flex flex-row text-blue-600 ml-auto my-auto cursor-pointer"
          onClick={() => {
            click("click_cta", {
              clickCopy: `${
                isOverviewVisible ? "Hide" : "Show"
              } content overview`,
            });
            setOverviewVisible((vis) => !vis);
          }}
        >
          <Text
            as="button"
            className="underline hover:font-medium whitespace-nowrap"
            variant={"sm-normal"}
          >
            {isOverviewVisible
              ? t("toggle.hide-overview")
              : t("toggle.show-overview")}
          </Text>
          {isOverviewVisible ? (
            <IconNavigationArrowUp />
          ) : (
            <IconNavigationArrowDown />
          )}
        </div>
      </div>
      {isOverviewVisible && (
        <div className="flex flex-col gap-6 pb-3 px-3">
          <div role="group">
            <Text as="h2" variant="lg-medium">
              {t("overview.title")}
            </Text>
            <Text as="p">{description}</Text>
          </div>
          <div role="group">
            <Text as="h2" variant="lg-medium">
              {t("learning-sequence.title", { count: learningSequence.length })}
            </Text>
            <ol
              aria-label={t("learning-sequence.a11y")}
              className="pl-6 list-decimal"
            >
              {learningSequence.map((item) => (
                <li key={item.id}>
                  <Text as="p">{item.title}</Text>
                </li>
              ))}
            </ol>
          </div>

          <div className="flex items-center gap-4 bg-neutral-100 p-200 rounded-lg">
            <IconCommunicationInformation className="w-10" />
            <Text as="p" className="m-0! leading-tight" variant="base-medium">
              Plan this unit to get links to all learning resources.
            </Text>
          </div>
        </div>
      )}
    </div>
  );
};
